import ReactGA from 'react-ga4';

// Google Analytics goes through this module only. react-ga (Universal Analytics, which Google
// stopped processing in 2024) was replaced by react-ga4 (gtag.js) in PANEL-1530; the measurement ID
// is REACT_APP_GA_TRACKING_ID and must be a GA4 "G-…" ID.
//
// Nothing is sent until initAnalytics() ran: react-ga warned and dropped such calls, while
// react-ga4 would queue them into window.dataLayer forever when GA is disabled (no ID configured).
//
// initAnalytics() runs only once the member granted analytics on the cookie banner (utils/cookieConsent,
// PANEL-1554), and the portal offers no marketing category. Consent Mode has to say so: without a
// default, gtag treats ad_storage, ad_user_data and ad_personalization as granted. The banner script
// sends Consent Mode only for tools it loads itself, and GA comes through the portal's own template.
const CONSENT_DEFAULT = {
  analytics_storage: 'granted',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

// SE-GA-KODY-URL (PANEL-1605). Page URLs carry identifiers: /:lang/activate/:host/:rmsid/:code
// (ActivationAccount), /:lang/unsubscribe/:host/:rmsid/:code (UnsubscribeAcoount) and
// /:lang/finished/:status/:project_id/:panelist_id/:respondent_guid (Finished), each also without
// the language segment (containers/App's LANGUAGE_LESS_SEGMENTS). Google gets the route pattern and
// nothing it was not told is safe: the language, the route table's sections and their known
// sub-pages stay, every positional parameter becomes its name, and every other segment, including
// all of an unknown path after its language, becomes :redacted. The query keeps only the utm_*
// campaign parameters, each value only when it is a campaign slug; the fragment never goes.
const ROUTE_SECTIONS = [
  'login', 'finished', 'activate', 'unsubscribe', 'home', 'affiliate', 'rewards', 'history',
  'referral-program', 'help', 'profile', 'privacy-policy', 'terms-and-conditions', 'gdpr-sar',
];
// The fixed sub-pages a section has in the route table (/:id/home/privacy-policy redirects,
// /:id/rewards/:thankyou? where Rewards only reacts to "thankyou").
const SUB_PAGES = {
  home: ['privacy-policy', 'terms-and-conditions'],
  rewards: ['thankyou'],
};
// The segments after the section, by position, as the screens read them. null = keep the value when
// it is a known survey status or a short number (the legacy 10), :status otherwise.
const ROUTE_PARAMS = {
  activate: [':host', ':rmsid', ':code'],
  unsubscribe: [':host', ':rmsid', ':code'],
  finished: [null, ':project_id', ':panelist_id', ':respondent_guid'],
};
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];

// The statuses the portal and its specs know. Any other word in that slot may be a name.
const STATUSES = [
  'success', 'completed', 'inprogress', 'fin', 'screenout', 'quotafull', 'overquota', 'terminate',
  'terminated', 'fail', 'failed', 'error', 'closed',
];
const NUMERIC_STATUS = /^\d{1,3}$/;
export const LANGUAGE = /^[a-z]{2,3}(?:[-_][a-z0-9]{2,4})?$/i;
// A campaign name is a lowercase slug with few digits (newsletter, oct-2026, portal_footer). Capitals
// mixed with digits, dots, '@' or a GUID's hex are what tokens, mail addresses and IDs look like.
const CAMPAIGN_VALUE = /^[a-z][a-z0-9_-]{0,39}$/;
const campaignValueIsSafe = (value) => CAMPAIGN_VALUE.test(value) && (value.match(/\d/g) || []).length <= 4;

// react-router matches paths case-insensitively and on decoded segments, so /en/Activate/… and
// /en/activat%65/… open the activation page too and must not slip past as an unknown path.
const decodeLower = (text) => {
  try {
    return decodeURIComponent(text || '').toLowerCase();
  } catch (e) {
    return null;
  }
};

const routeSection = (segment) => {
  const decoded = decodeLower(segment);
  return ROUTE_SECTIONS.indexOf(decoded) !== -1 ? decoded : null;
};

const language = (segment) => (LANGUAGE.test(segment) ? segment.toLowerCase() : ':lang');

const sanitizePath = (pathname) => {
  // Empty segments (//, a trailing slash) carry nothing and would split one page into several rows.
  const segments = pathname.split('/').filter(Boolean);
  let sectionAt = routeSection(segments[0]) ? 0 : -1;
  if (sectionAt === -1 && segments.length > 1 && routeSection(segments[1])) sectionAt = 1;
  if (sectionAt === -1) {
    if (!segments.length) return '/';
    // An unknown path (the 404 page): its language when it starts with one, nothing else.
    const first = LANGUAGE.test(segments[0]) ? segments[0].toLowerCase() : ':redacted';
    return '/' + [first].concat(segments.slice(1).map(() => ':redacted')).join('/');
  }

  const out = [];
  if (sectionAt === 1) out.push(language(segments[0]));
  const section = routeSection(segments[sectionAt]);
  out.push(section);
  const params = ROUTE_PARAMS[section];
  const subPages = SUB_PAGES[section] || [];
  segments.slice(sectionAt + 1).forEach((segment, i) => {
    if (!params) out.push(i === 0 && subPages.indexOf(decodeLower(segment)) !== -1 ? decodeLower(segment) : ':redacted');
    else if (params[i] === null) {
      const status = decodeLower(segment);
      out.push(STATUSES.indexOf(status) !== -1 || NUMERIC_STATUS.test(segment) ? status : ':status');
    }
    else out.push(params[i] || ':redacted');
  });
  return '/' + out.join('/');
};

// Parsed by hand rather than with URLSearchParams so ';' separates pairs too: a kept pair must
// never carry a second parameter glued to it.
const sanitizeSearch = (search) => {
  const kept = [];
  search.split(/[&;]/).forEach((pair) => {
    const at = pair.indexOf('=');
    const name = decodeLower(at === -1 ? pair : pair.slice(0, at));
    if (CAMPAIGN_PARAMS.indexOf(name) === -1) return;
    let value;
    try {
      value = decodeURIComponent((at === -1 ? '' : pair.slice(at + 1)).replace(/\+/g, ' '));
    } catch (e) {
      value = null;
    }
    kept.push(name + '=' + (value !== null && campaignValueIsSafe(value) ? value : ':redacted'));
  });
  return kept.length ? '?' + kept.join('&') : '';
};

// pathAndSearch: what the reporters pass, location.pathname + location.search. Everything after
// the first '?' is the query, a second '?' included.
export const sanitizeAnalyticsLocation = (pathAndSearch) => {
  const withoutHash = String(pathAndSearch || '').split('#')[0];
  const queryAt = withoutHash.indexOf('?');
  const pathname = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const search = queryAt === -1 ? '' : withoutHash.slice(queryAt + 1);
  return sanitizePath(pathname) + sanitizeSearch(search);
};

const analyticsPageLocation = (pathAndSearch) => window.location.origin + sanitizeAnalyticsLocation(pathAndSearch);

// A portal referrer is one of its own pages and gets the same treatment; another web site is
// reduced to its origin, which is all GA4 needs for the traffic source. No referrer stays empty.
export const sanitizeAnalyticsReferrer = (referrer) => {
  if (!referrer) return '';
  let url;
  try {
    url = new URL(referrer);
  } catch (e) {
    return '';
  }
  // An app or about:blank referrer has the opaque origin "null": nothing to attribute.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
  if (url.origin !== window.location.origin) return url.origin + '/';
  return url.origin + sanitizeAnalyticsLocation(url.pathname + url.search);
};

export const initAnalytics = (measurementId) => {
  // Before initialize(): react-ga4 pushes it to window.dataLayer ahead of its `js` and `config`.
  ReactGA.gtag('consent', 'default', CONSENT_DEFAULT);
  // Page views are sent by hand on every navigation (below), so gtag's automatic one on `config`
  // would double-count the landing page. page_location and page_referrer go into the config
  // sanitized (SE-GA-KODY-URL): gtag fills them from document.location / document.referrer for
  // every event of the tag otherwise.
  const pageLocation = analyticsPageLocation(window.location.pathname + window.location.search);
  const pageReferrer = sanitizeAnalyticsReferrer(document.referrer);
  ReactGA.initialize(measurementId, {
    gtagOptions: { send_page_view: false, page_location: pageLocation, page_referrer: pageReferrer },
  });
  // Custom dimension 1 = network status, as in the UA setup. In GA4 it arrives as the event
  // parameter `dimension1` and needs a custom dimension registered for it to show in reports.
  ReactGA.set({ dimension1: 'online' });
};

// True when the page view was handed to gtag. The path is sanitized here, the one place every page
// view goes through; the sanitized URL is the hit's page_location and is also `set`, so the events
// that follow the page view report it rather than document.location.
const sendPageview = (path) => {
  if (!ReactGA.isInitialized) return false;
  const page = sanitizeAnalyticsLocation(path);
  const location = window.location.origin + page;
  ReactGA.set({ page_location: location });
  ReactGA.send({ hitType: 'pageview', page, location });
  return true;
};

// A page view outside the deduplicated pair below (the public routes in containers/App).
export const reportPageview = (path) => {
  sendPageview(path);
};

// fields: { category, action, label, value } — the UA event shape react-ga4 still accepts.
export const reportEvent = (fields) => {
  if (!ReactGA.isInitialized) return;
  ReactGA.event(fields);
};

// W3A-T9-1. The first load of a protected path was reported TWICE.
//
// src/index.js calls initGoogleAnalytics() after ReactDOM.render, and it initializes GA and then
// reports window.location. React 16 flushes passive effects on a later tick, so
// AuthorizedShell's own effect (containers/App/PrivateRoute.js) runs AFTER that — and both calls
// land after initialize, so both are sent, for the same URL.
//
// Neither call can simply go away. Dropping the bootstrap one would leave every PUBLIC page
// unreported, because only the guarded route has a reporter. Skipping the route's effect on first
// mount would key the fix on mount order, which is the thing that varies.
//
// So the bootstrap call announces the path it sent, and the route reporter swallows exactly ONE
// report that matches it. Deliberately not a general "same path as last time" filter: a push to
// the URL you are already on IS a navigation, and PVR-2 requires it to be reported. The marker is
// cleared on first use, so only the initial duplicate is affected and every later navigation —
// including a repeat of the same URL — reports normally.
let bootstrapReported = null;

export const reportBootstrapPageview = (path) => {
  // Marked only AFTER a send that did not throw. Setting it first meant a page view send that
  // threw still armed the suppression, so the route reporter stayed quiet too and the load was
  // reported ZERO times — worse than the double report this exists to fix.
  // Nor when nothing was sent (GA not initialized): there is no duplicate to swallow then.
  if (sendPageview(path)) bootstrapReported = path;
};

export const reportRoutePageview = (path) => {
  const alreadySent = bootstrapReported === path;
  bootstrapReported = null;
  if (alreadySent) return false;
  sendPageview(path);
  return true;
};

// Tests only: the module-level marker outlives a single test otherwise, and a leaked marker makes
// a later assertion pass for the wrong reason.
export const resetPageviewDedupe = () => {
  bootstrapReported = null;
};
